/**
 * soundManager — thin Howler.js wrapper for Bra 3-Way SFX + ambient music.
 *
 * Loads on first `play()` (lazy), respects browser autoplay policy via
 * `Howler.autoUnlock` (default true — unlocks on the first user gesture),
 * persists user prefs in localStorage, exposes a `subscribe(cb)` for
 * reactive UI (mute button icon, settings sliders).
 *
 * Volume model:
 *   final = masterVolume × (music/sfx category volume) × sound baseline
 *
 * SFX are one-shot Howls (`play()` returns a howlId).
 * Music (ambient_loop) is a single long-lived Howl with `loop: true`.
 */
import { Howl, Howler } from "howler";

// Registry — order in this object is the order they appear in the file list.
// Each entry: { file, category, baseVolume, loop? }
const BANK = {
  chip_click:      { file: "chip_click.wav",     category: "sfx", baseVolume: 0.50 },
  spin_start:      { file: "spin_start.wav",     category: "sfx", baseVolume: 0.60 },
  reveal_tick:     { file: "reveal_tick.wav",    category: "sfx", baseVolume: 0.40, loop: true },
  reveal_chime:    { file: "reveal_chime.wav",   category: "sfx", baseVolume: 0.60 },
  win_celebration: { file: "win_celebration.wav", category: "sfx", baseVolume: 0.80 },
  lose_soft:       { file: "lose_soft.wav",      category: "sfx", baseVolume: 0.60 },
  coin_pile:       { file: "coin_pile.wav",      category: "sfx", baseVolume: 0.50 },
  model_swoosh:    { file: "model_swoosh.wav",   category: "sfx", baseVolume: 0.30 },
  countdown_tick:  { file: "countdown_tick.wav", category: "sfx", baseVolume: 0.40 },
  ambient_loop:    { file: "ambient_loop.mp3",   category: "music", baseVolume: 0.30, loop: true },
};

const KEYS = {
  muted:              "sound.muted",
  master:             "sound.master_volume",
  music:              "sound.music_volume",
  sfx:                "sound.sfx_volume",
  music_enabled:      "sound.music_enabled",
  model_swoosh_on:    "sound.model_swoosh_enabled",
};

const DEFAULTS = {
  muted: false,
  master: 0.7,
  music: 0.3,
  sfx: 0.7,
  music_enabled: false,          // per spec: ambient off by default
  model_swoosh_enabled: false,   // per spec: off by default
};

function readBool(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    if (v === null) return fallback;
    return v === "1" || v === "true";
  } catch { return fallback; }
}
function readFloat(key, fallback) {
  try {
    const v = parseFloat(localStorage.getItem(key));
    return Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : fallback;
  } catch { return fallback; }
}
function writeBool(key, v) { try { localStorage.setItem(key, v ? "1" : "0"); } catch {} }
function writeFloat(key, v) { try { localStorage.setItem(key, String(v)); } catch {} }

class SoundManager {
  constructor() {
    this.state = {
      muted:                readBool(KEYS.muted, DEFAULTS.muted),
      master:               readFloat(KEYS.master, DEFAULTS.master),
      music:                readFloat(KEYS.music, DEFAULTS.music),
      sfx:                  readFloat(KEYS.sfx, DEFAULTS.sfx),
      musicEnabled:         readBool(KEYS.music_enabled, DEFAULTS.music_enabled),
      modelSwooshEnabled:   readBool(KEYS.model_swoosh_on, DEFAULTS.model_swoosh_enabled),
    };
    this.howls = {};              // name -> Howl instance (lazy)
    this.playingIds = {};         // name -> current howlId (for stopping loops)
    this.listeners = new Set();
    this.debug = false;
    if (typeof window !== "undefined") {
      // Any error should stay silent so audio issues never crash gameplay.
      Howler.autoUnlock = true;
    }
    // Apply initial mute
    if (typeof Howler !== "undefined") Howler.mute(this.state.muted);
  }

  // ---- state / persistence ------------------------------------------------
  _emit() { this.listeners.forEach((cb) => { try { cb(this.getState()); } catch {} }); }
  subscribe(cb) { this.listeners.add(cb); return () => this.listeners.delete(cb); }
  getState() { return { ...this.state }; }

  // ---- volume management --------------------------------------------------
  _volumeFor(name) {
    const entry = BANK[name];
    if (!entry) return 0;
    const cat = entry.category === "music" ? this.state.music : this.state.sfx;
    return this.state.master * cat * entry.baseVolume;
  }
  _reapplyVolumes() {
    Object.entries(this.howls).forEach(([name, howl]) => {
      try { howl.volume(this._volumeFor(name)); } catch {}
    });
  }
  setMasterVolume(v) {
    this.state.master = Math.max(0, Math.min(1, v));
    writeFloat(KEYS.master, this.state.master);
    this._reapplyVolumes(); this._emit();
  }
  setMusicVolume(v) {
    this.state.music = Math.max(0, Math.min(1, v));
    writeFloat(KEYS.music, this.state.music);
    this._reapplyVolumes(); this._emit();
  }
  setSfxVolume(v) {
    this.state.sfx = Math.max(0, Math.min(1, v));
    writeFloat(KEYS.sfx, this.state.sfx);
    this._reapplyVolumes(); this._emit();
  }
  setMusicEnabled(on) {
    this.state.musicEnabled = !!on;
    writeBool(KEYS.music_enabled, this.state.musicEnabled);
    if (this.state.musicEnabled) this.play("ambient_loop");
    else this.stop("ambient_loop");
    this._emit();
  }
  setModelSwooshEnabled(on) {
    this.state.modelSwooshEnabled = !!on;
    writeBool(KEYS.model_swoosh_on, this.state.modelSwooshEnabled);
    this._emit();
  }
  isModelSwooshEnabled() { return this.state.modelSwooshEnabled; }

  // ---- mute ---------------------------------------------------------------
  mute()      { this.state.muted = true;  writeBool(KEYS.muted, true);  Howler.mute(true);  this._emit(); }
  unmute()    { this.state.muted = false; writeBool(KEYS.muted, false); Howler.mute(false); this._emit(); }
  toggleMute() { this.state.muted ? this.unmute() : this.mute(); }
  isMuted()   { return this.state.muted; }
  isMusicEnabled() { return this.state.musicEnabled; }

  // ---- loading / playback -------------------------------------------------
  _load(name) {
    const entry = BANK[name];
    if (!entry) return null;
    if (this.howls[name]) return this.howls[name];
    const howl = new Howl({
      src: [`/sounds/${entry.file}`],
      loop: !!entry.loop,
      volume: this._volumeFor(name),
      html5: entry.category === "music", // stream long ambient via HTML5 to reduce memory
      preload: true,
    });
    this.howls[name] = howl;
    return howl;
  }

  play(name, opts = {}) {
    if (!BANK[name]) { if (this.debug) console.warn(`[sound] unknown "${name}"`); return null; }
    // Ambient is gated by musicEnabled — refuse to start it if disabled.
    if (name === "ambient_loop" && !this.state.musicEnabled) return null;
    const howl = this._load(name);
    if (!howl) return null;
    try {
      const id = howl.play();
      this.playingIds[name] = id;
      if (opts.delay) {
        // Consumer-side scheduling (Howler doesn't have native start-delay for one-shots).
        // Not used internally; provided for convenience.
      }
      if (this.debug) console.log(`[sound] play ${name}`);
      return id;
    } catch (e) {
      if (this.debug) console.warn(`[sound] play failed ${name}`, e);
      return null;
    }
  }

  stop(name) {
    const howl = this.howls[name];
    if (!howl) return;
    try { howl.stop(); } catch {}
    delete this.playingIds[name];
    if (this.debug) console.log(`[sound] stop ${name}`);
  }

  stopAll() {
    Object.keys(this.howls).forEach((n) => this.stop(n));
  }

  reset() {
    this.state = {
      muted:               DEFAULTS.muted,
      master:              DEFAULTS.master,
      music:               DEFAULTS.music,
      sfx:                 DEFAULTS.sfx,
      musicEnabled:        DEFAULTS.music_enabled,
      modelSwooshEnabled:  DEFAULTS.model_swoosh_enabled,
    };
    Object.entries(KEYS).forEach(([, k]) => { try { localStorage.removeItem(k); } catch {} });
    Howler.mute(false);
    this._reapplyVolumes();
    this.stop("ambient_loop");
    this._emit();
  }
}

const soundManager = new SoundManager();

// Expose for browser debugging / manual QA
if (typeof window !== "undefined") window.__sound = soundManager;

export default soundManager;
export { BANK };
